import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { fmtTRY } from "@/lib/format";

/**
 * Reusable invoice-line editor + live totals.
 *
 * A single source of truth for the "kalem tablosu + Ara Toplam / KDV /
 * Genel Toplam" block used by the standalone Faturalar screen AND by
 * the inline invoice section embedded in the New Job dialog.
 *
 * Contract:
 *   * ``items`` is an array of *string-typed* line objects — quantity /
 *     unit_price / vat_rate remain strings while the user is typing so
 *     the caret does not jump.  On submit, the caller converts to
 *     numbers.
 *   * The caller owns the state (uncontrolled component pattern):
 *     ``setItems`` receives the entire next array.  This keeps parent
 *     forms free to serialize / validate the items alongside their own
 *     fields.
 *   * ``vatRates`` is the *active* rate list (result of
 *     GET /api/vat-rates?status=active).  We deduplicate against the
 *     currently-selected value so a passive snapshot inside an edited
 *     invoice is still displayed even after the rate was retired.
 */
export function InvoiceItemsEditor({ items, setItems, vatRates, testIdPrefix = "invoice-item" }) {
  const emptyItem = () => {
    const def =
      (vatRates ?? []).find((r) => r.is_default) ??
      (vatRates ?? [])[0] ??
      { rate: 20 };
    return {
      description: "",
      quantity: "1",
      unit_price: "",
      vat_rate: String(def.rate),
    };
  };

  const totals = useMemo(() => {
    let ex = 0, vat = 0;
    for (const it of items) {
      const q = parseFloat(it.quantity) || 0;
      const u = parseFloat(it.unit_price) || 0;
      const r = parseFloat(it.vat_rate) || 0;
      const lineEx = q * u;
      ex += lineEx;
      vat += lineEx * (r / 100);
    }
    return { ex, vat, gt: ex + vat };
  }, [items]);

  const setItem = (idx, patch) => {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };
  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (idx) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  // Merge passive-but-referenced snapshot rates into the dropdown so
  // edit forms don't lose the selected value after a rate change.
  const dropdownRates = useMemo(() => {
    const map = new Map(
      (vatRates ?? []).map((r) => [String(r.rate), r]),
    );
    for (const it of items) {
      if (it.vat_rate && !map.has(String(it.vat_rate))) {
        map.set(String(it.vat_rate), {
          id: `snapshot_${it.vat_rate}`,
          rate: parseFloat(it.vat_rate),
          status: "passive",
          is_default: false,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.rate - b.rate);
  }, [vatRates, items]);

  return (
    <>
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Fatura Kalemleri</div>
          <Button type="button" size="sm" variant="outline" onClick={addItem}
                  data-testid={`${testIdPrefix}-add-button`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Yeni Kalem
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-semibold">Mal / Hizmet</th>
                <th className="px-3 py-2 font-semibold w-24">Miktar</th>
                <th className="px-3 py-2 font-semibold w-32">Birim Fiyat</th>
                <th className="px-3 py-2 font-semibold w-28">KDV</th>
                <th className="px-3 py-2 font-semibold w-32 text-right">Toplam</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it, idx) => {
                const q = parseFloat(it.quantity) || 0;
                const u = parseFloat(it.unit_price) || 0;
                const r = parseFloat(it.vat_rate) || 0;
                const lineTotal = q * u * (1 + r / 100);
                return (
                  <tr key={idx} data-testid={`${testIdPrefix}-row-${idx}`}>
                    <td className="px-3 py-2">
                      <Input
                        value={it.description}
                        onChange={(e) => setItem(idx, { description: e.target.value })}
                        placeholder="Örn: Danışmanlık"
                        className="h-9 rounded-md"
                        data-testid={`${testIdPrefix}-description-${idx}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.01" min="0" value={it.quantity}
                        onChange={(e) => setItem(idx, { quantity: e.target.value })}
                        className="h-9 rounded-md"
                        data-testid={`${testIdPrefix}-quantity-${idx}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.01" min="0" value={it.unit_price}
                        onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                        className="h-9 rounded-md"
                        data-testid={`${testIdPrefix}-unit-price-${idx}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Select value={it.vat_rate}
                              onValueChange={(v) => setItem(idx, { vat_rate: v })}>
                        <SelectTrigger className="h-9 rounded-md"
                                       data-testid={`${testIdPrefix}-vat-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dropdownRates.map((vr) => (
                            <SelectItem key={String(vr.id)} value={String(vr.rate)}>%{vr.rate}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtTRY(lineTotal)}</td>
                    <td className="px-3 py-2">
                      <Button type="button" size="icon" variant="ghost"
                              className="h-7 w-7 text-red-600 hover:bg-red-50"
                              onClick={() => removeItem(idx)}
                              disabled={items.length === 1}
                              data-testid={`${testIdPrefix}-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ml-auto max-w-sm bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2 text-sm">
        <TotalRow label="Ara Toplam" value={fmtTRY(totals.ex)} testId={`${testIdPrefix}-total-ex-vat`} />
        <TotalRow label="Toplam KDV" value={fmtTRY(totals.vat)} testId={`${testIdPrefix}-total-vat`} />
        <div className="h-px bg-slate-200" />
        <TotalRow label="Genel Toplam" value={fmtTRY(totals.gt)} bold testId={`${testIdPrefix}-total-grand`} />
      </div>
    </>
  );
}

/** Serialize the string-typed editor rows into the backend payload
 *  shape.  Returns [{description, quantity, unit_price, vat_rate}]. */
export function serializeInvoiceItems(items) {
  return items.map((it) => ({
    description: (it.description || "").trim(),
    quantity:    parseFloat(it.quantity)   || 0,
    unit_price:  parseFloat(it.unit_price) || 0,
    vat_rate:    parseFloat(it.vat_rate)   || 0,
  }));
}

/** Build an empty item row from the tenant's active vat_rates list. */
export function makeEmptyInvoiceItem(vatRates) {
  const def =
    (vatRates ?? []).find((r) => r.is_default) ??
    (vatRates ?? [])[0] ??
    { rate: 20 };
  return {
    description: "",
    quantity: "1",
    unit_price: "",
    vat_rate: String(def.rate),
  };
}

function TotalRow({ label, value, bold, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-800"}>{value}</span>
    </div>
  );
}
